import { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { MetricCard, MetricCardSkeleton, StatCard, StatCardSkeleton } from "../components";
import { useSession } from "../auth/client";
import { useFilters } from "../context/FilterContext";

const PhoneIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  </svg>
);

const DollarIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const DialIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.25 4.5l7.5 7.5-7.5 7.5" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

function getYesterdayDate(): string {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const year = yesterday.getFullYear();
  const month = String(yesterday.getMonth() + 1).padStart(2, "0");
  const day = String(yesterday.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value || 0);
}

interface EodRecordBase {
  timestamp: string;
  aiSentiment?: "positive" | "neutral" | "negative" | "mixed" | null;
  aiSentimentSummary?: string | null;
}

interface CloserEODRecord extends EodRecordBase {
  closerName: string;
  cashGenerated: number;
  closes: number;
  showedUp: number;
  offersMade: number;
  noShows: number;
  reschedules: number;
}

interface SetterEODRecord extends EodRecordBase {
  setterName: string;
  dials: number;
  connected: number;
  leadsBooked: number;
}

interface DailyReportData {
  date: string;
  aggregateSentiment: {
    closerSentiment?: "positive" | "neutral" | "negative" | "mixed" | null;
    closerSummary?: string | null;
    closerCount: number;
    setterSentiment?: "positive" | "neutral" | "negative" | "mixed" | null;
    setterSummary?: string | null;
    setterCount: number;
  };
  eoc: {
    totalCalls: number;
    cashCollected: number;
    closedCount: number;
  };
  setter: {
    totalDials: number;
    totalConnected: number;
    totalLeadsBooked: number;
    settersCount: number;
  };
  closer: {
    totalCashGenerated: number;
    totalCloses: number;
    totalShowedUp: number;
    totalOffersMade: number;
    totalNoShows: number;
    totalReschedules: number;
    completedCount: number;
    eodCompletionRate: number;
  };
  eodCompletion: {
    total: number;
    completed: number;
    rate: number;
    closers: {
      total: number;
      completed: number;
      missing: string[];
    };
    setters: {
      total: number;
      completed: number;
      missing: string[];
    };
    missingEODs: string[];
  };
  closerEODRecords: CloserEODRecord[];
  setterEODRecords: SetterEODRecord[];
}

function SentimentScorecard({
  title,
  sentiment,
  summary,
  count,
  index,
}: {
  title: string;
  sentiment?: "positive" | "neutral" | "negative" | "mixed" | null;
  summary?: string | null;
  count: number;
  index: number;
}) {
  const badgeClasses = {
    positive: "bg-emerald-500/15 text-emerald-400",
    neutral: "bg-slate-500/15 text-slate-300",
    negative: "bg-red-500/15 text-red-400",
    mixed: "bg-amber-500/15 text-amber-400",
  } as const;

  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.45 + index * 0.08 }}
      className="rounded-2xl border border-border bg-card p-5 md:p-6"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="text-base font-semibold md:text-lg">{title}</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {count} {count === 1 ? "report" : "reports"}
          </p>
        </div>
        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold uppercase tracking-[0.12em] ${sentiment ? badgeClasses[sentiment] : "bg-muted text-muted-foreground"}`}>
          {sentiment || "Pending"}
        </span>
      </div>
      <p className="mt-4 text-sm leading-6 text-muted-foreground">
        {summary || "Sentiment analysis will populate after enough EOD submissions are available for the selected date."}
      </p>
    </motion.div>
  );
}

function RecordTable({
  title,
  subtitle,
  columns,
  rows,
}: {
  title: string;
  subtitle: string;
  columns: string[];
  rows: Array<Array<string | number>>;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.7 }}
      className="overflow-hidden rounded-xl border border-border bg-card"
    >
      <div className="border-b border-border p-4">
        <h3 className="text-lg font-semibold">{title}</h3>
        <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[680px]">
          <thead>
            <tr className="border-b border-border bg-muted/40">
              {columns.map((column) => (
                <th key={column} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={`${title}-${rowIndex}`} className={rowIndex % 2 === 0 ? "" : "bg-muted/15"}>
                {row.map((cell, cellIndex) => (
                  <td key={`${title}-${rowIndex}-${cellIndex}`} className="px-4 py-3 text-sm">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </motion.div>
  );
}

export function DailyReport() {
  const { data: session } = useSession();
  const { offers, offer } = useFilters();
  const [selectedDate, setSelectedDate] = useState<string>(getYesterdayDate);
  const [data, setData] = useState<DailyReportData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const selectedOffers = useMemo(() => (
    offers.length > 0 ? offers : (offer ? [offer] : [])
  ), [offer, offers]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("date", selectedDate);
      if (selectedOffers.length > 0) {
        params.set("offer", selectedOffers.join(","));
      }
      const response = await fetch(`/api/daily-report?${params.toString()}`);
      const result = await response.json();
      if (result.success) {
        setData(result.data);
      }
    } catch (error) {
      console.error("Failed to fetch daily report:", error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedDate, selectedOffers]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    const firstName = session?.user?.name?.split(" ")[0] || "";
    const timeGreeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
    return firstName ? `${timeGreeting}, ${firstName}` : timeGreeting;
  }, [session?.user?.name]);

  if (isLoading && !data) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="h-8 w-52 animate-pulse rounded bg-muted" />
            <div className="h-4 w-72 animate-pulse rounded bg-muted" />
          </div>
          <div className="h-10 w-40 animate-pulse rounded bg-muted" />
        </div>
        <div className="grid gap-6 md:grid-cols-4">
          {[0, 1, 2, 3].map((i) => <MetricCardSkeleton key={i} index={i} />)}
        </div>
        <div className="grid gap-4 md:grid-cols-4">
          {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => <StatCardSkeleton key={i} index={i} />)}
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between"
      >
        <div>
          <h1 className="text-2xl font-bold tracking-tight md:text-3xl">{greeting}</h1>
          <p className="mt-1 text-sm text-muted-foreground">Daily Report for the currently selected offer.</p>
        </div>
        <input
          type="date"
          value={selectedDate}
          onChange={(event) => setSelectedDate(event.target.value)}
          className="h-10 rounded-lg border border-input bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
        />
      </motion.div>

      <div className="grid gap-6 md:grid-cols-4">
        <MetricCard label="Calls Today" value={String(data.eoc.totalCalls)} icon={<PhoneIcon />} color="blue" index={0} />
        <MetricCard label="Cash Collected" value={formatCurrency(data.eoc.cashCollected)} icon={<DollarIcon />} color="green" index={1} />
        <MetricCard label="Dials Today" value={String(data.setter.totalDials)} icon={<DialIcon />} color="brand" index={2} />
        <MetricCard label="Closes Today" value={String(data.eoc.closedCount)} icon={<CheckCircleIcon />} color="green" index={3} />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35 }}
        className="rounded-2xl border border-border bg-card p-6"
      >
        <div className="mb-4 flex items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold">EOD Completion</h2>
            <p className="text-sm text-muted-foreground">
              {data.eodCompletion.completed} of {data.eodCompletion.total} reps submitted for this date.
            </p>
          </div>
          <p className="text-2xl font-semibold">{data.eodCompletion.rate}%</p>
        </div>
        <div className="h-3 overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-[var(--brand-primary)] transition-all"
            style={{ width: `${Math.min(100, Math.max(0, data.eodCompletion.rate))}%` }}
          />
        </div>
      </motion.div>

      <div className="grid gap-6 md:grid-cols-2">
        <SentimentScorecard
          title="Closer Team Sentiment"
          sentiment={data.aggregateSentiment.closerSentiment}
          summary={data.aggregateSentiment.closerSummary}
          count={data.aggregateSentiment.closerCount}
          index={0}
        />
        <SentimentScorecard
          title="Setter Team Sentiment"
          sentiment={data.aggregateSentiment.setterSentiment}
          summary={data.aggregateSentiment.setterSummary}
          count={data.aggregateSentiment.setterCount}
          index={1}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <StatCard label="Total Dials" value={String(data.setter.totalDials)} index={0} />
        <StatCard label="Connected" value={String(data.setter.totalConnected)} index={1} />
        <StatCard label="Leads Booked" value={String(data.setter.totalLeadsBooked)} index={2} />
        <StatCard label="Active Setters" value={String(data.setter.settersCount)} index={3} />
      </div>

      <div className="grid gap-4 md:grid-cols-6">
        <StatCard label="Showed Up" value={String(data.closer.totalShowedUp)} index={4} />
        <StatCard label="Offers Made" value={String(data.closer.totalOffersMade)} index={5} />
        <StatCard label="Closes" value={String(data.closer.totalCloses)} index={6} />
        <StatCard label="Cash (EOD)" value={formatCurrency(data.closer.totalCashGenerated)} index={7} />
        <StatCard label="No Shows" value={String(data.closer.totalNoShows)} index={8} />
        <StatCard label="Reschedules" value={String(data.closer.totalReschedules)} index={9} />
      </div>

      {data.eodCompletion.missingEODs.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55 }}
          className="rounded-xl border border-red-500/25 bg-red-500/5 p-5"
        >
          <h3 className="text-lg font-semibold text-red-400">Missing EODs</h3>
          <div className="mt-4 flex flex-wrap gap-2">
            {data.eodCompletion.missingEODs.map((name) => (
              <span key={name} className="rounded-full bg-red-500/10 px-3 py-1 text-sm text-red-300">
                {name}
              </span>
            ))}
          </div>
        </motion.div>
      )}

      {data.closerEODRecords.length > 0 && (
        <RecordTable
          title="Closer EODs"
          subtitle="Completed closer reports for the selected date."
          columns={["Closer", "Showed Up", "Offers Made", "Closes", "Cash Generated", "No Shows", "Reschedules"]}
          rows={data.closerEODRecords.map((record) => [
            record.closerName,
            record.showedUp,
            record.offersMade,
            record.closes,
            formatCurrency(record.cashGenerated),
            record.noShows,
            record.reschedules,
          ])}
        />
      )}

      {data.setterEODRecords.length > 0 && (
        <RecordTable
          title="Setter EODs"
          subtitle="Completed setter reports for the selected date."
          columns={["Setter", "Dials", "Connected", "Leads Booked"]}
          rows={data.setterEODRecords.map((record) => [
            record.setterName,
            record.dials,
            record.connected,
            record.leadsBooked,
          ])}
        />
      )}
    </div>
  );
}
